import { useState, useEffect } from "react";

import { InputAdornment, IconButton, Autocomplete } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faTimes } from "@fortawesome/free-solid-svg-icons";

import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import ButtonsSelect from "./ButtonsSelect/ButtonsSelect";

import { Period, PeriodType } from "../../models/documents/period/period";

import { Props } from "./interfaces";
import { TextField } from "./styles";
import { convertDateToUTC } from "src/utils/time-utils";

const options = [
  {
    title: "Without period",
    value: "withoutPeriod",
  },
  {
    title: "Current Calendar year",
    value: Period.makePeriod(new Date().getFullYear(), PeriodType.CalendaristicYear, 1),
  },
  {
    title: "Current Fiscal year",
    value: Period.makePeriod(new Date().getFullYear(), PeriodType.FiscalYear, 1),
  },
  {
    title: "Current Quarter period",
    value: Period.makePeriod(new Date().getFullYear(), PeriodType.Quarter, Math.ceil((new Date().getMonth() + 1) / 3)),
  },
  {
    title: "Current Month period",
    value: Period.makePeriod(new Date().getFullYear(), PeriodType.Month, new Date().getMonth() + 1),
  },
];

export default function CustomDatePicker({
  period,
  handlePeriodChange,
  key,
  disabled = false,
  customStyle,
  disableSuggestions = false,
}: Props) {
  const [date, setDate] = useState<Date>();
  const [inputValue, setInputValue] = useState<string>("");

  const [value, setValue] = useState<any>(null);

  const makePeriod = (aPeriod?: Period | null) =>
    aPeriod
      ? Period.makePeriod(aPeriod.year, aPeriod.type, aPeriod.periodNumber)
      : Period.makePeriod(new Date().getFullYear(), PeriodType.CalendaristicYear, 1);

  const onDateChange = (aDate: Date, type: PeriodType) => {
    const newPeriod = new Period(aDate, type);
    const aPeriod = makePeriod(newPeriod);

    setValue(null);

    handlePeriodChange({
      ...aPeriod,
      startDate: convertDateToUTC(aPeriod.startDate),
      endDate: convertDateToUTC(aPeriod.endDate),
    } as Period);
  };

  useEffect(() => {
    if (!!period) {
      const computedPeriod = makePeriod(period);
      if (inputValue !== computedPeriod.value) {
        setInputValue(computedPeriod.value);
      }

      setDate(computedPeriod.getDate());
    } else {
      setInputValue(options[0].title);
      setValue(options[0]);
      setDate(undefined);
    }
  }, [period, inputValue]);

  return (
    <>
      <Autocomplete
        key={key}
        options={options}
        getOptionLabel={(option) => option.title}
        value={value}
        disabled={disableSuggestions}
        onChange={(event: any, newValue) => {
          if (newValue?.value === options[0].value) {
            handlePeriodChange(null);
            setValue(newValue);
          } else {
            handlePeriodChange(newValue?.value);
            setValue(newValue);
          }
        }}
        inputValue={period || !!inputValue ? inputValue : ""}
        sx={{ caretColor: "white" }}
        renderInput={(params) => (
          <TextField
            {...params}
            title={"Click to get suggestions"}
            value={period || !!inputValue ? inputValue : ""}
            label="Period"
            variant="outlined"
            disabled={disabled}
            sx={{
              ...customStyle,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment
                  position="start"
                  title={"Select period"}
                >
                  {!disabled && (
                    <SimplePopover
                      buttonContent={<FontAwesomeIcon icon={faCalendarAlt} />}
                      buttonType="icon"
                      buttonVariant="text"
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      <ButtonsSelect
                        date={date}
                        onDateChange={onDateChange}
                        radioValue={period?.type ?? PeriodType.CalendaristicYear}
                      />
                    </SimplePopover>
                  )}
                </InputAdornment>
              ),
              endAdornment: period && (
                <InputAdornment
                  position="end"
                  title={"Remove period"}
                >
                  {!disabled && (
                    <IconButton
                      onClick={() => {
                        setValue(options[0]);
                        handlePeriodChange(null);
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </>
  );
}
